import React, {useContext, useEffect, useState} from 'react';
import {useHistory, useParams, withRouter} from 'react-router-dom';
import {
    CircularProgress,
    IconButton,
    Box,
    Popover,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import {useDeleteEndpoint, useGetUserForm} from 'hooks/';
import FormReview from 'components/forms/sections/Review/ReviewComponent';
import SSLDisplay from 'components/forms/sections/Review/SSLDisplay';
import {Alert} from '@material-ui/lab';
import {H3} from 'components/TypographyShortcuts';
import useBrowserTitle from 'hooks/useBrowserTitle';
import {Delete, Edit, MoreHoriz} from '@material-ui/icons';
import {ConfirmPrompt} from 'components/forms/Prompt';
import {AppCtx} from 'context/AppContext.jsx';
import {PageTab, WidgetCard} from 'components/index.js';
import usePostBin from 'hooks/usePostBin.js';
import {deepFormTransform} from 'utils/objects.js';
import debugLogger from 'utils/logger';

//need to figure out how to send unique ssl document types
const typeToTitle = {
    cg719s: 'CG719S Form',
    cod: 'Certificate of Discharge',
    ssl: 'Sea Service Letter on Official Letterhead',
    // noaatoss: 'NOAA Transcript of Sea Service',
    // armytoss: 'Army Transcript of Sea Service',
    uscg_aops: 'US Coast Guard AOPS',
    courses: 'Training Certifications',
    cpr: 'CPR/First Aid Certifications',
    nvic: 'STCW NVIC',
    supporting_document: 'Supporting Document',
    tankerman: 'Tankerman Letter',
};

const HeaderBar = ({title, docType, docId, formData, goBack}) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const [showConfirm, setShowConfirm] = useState(false);
    const deleteDoc = useDeleteEndpoint(`/api/1/user/${docType}/${docId}`);
    const {setSnackProps, setSnackOpen, setFormRedirect} = useContext(AppCtx);
    const postBin = usePostBin();
    const history = useHistory();

    const handleConfirmDelete = () => {
        setShowConfirm(false);
        deleteDoc.destroy();
    };

    const handleEdit = () => {
        const stateMachineState = JSON.parse(
            window.localStorage.getItem('__STATE_MACHINE__')
        );
        const transformedFormData = {
            user_document_post_response: {
                status: 'Done',
                user_document: formData.user_document,
                user_images: formData.user_images,
            },
            ...deepFormTransform(formData.form_data),
        };
        postBin.post({
            url: `/forms/${docType}/start`,
            form_state_json: {
                ...stateMachineState,
                forms: {
                    ...stateMachineState?.forms,
                    [docType]: transformedFormData,
                },
            },
        });
    };

    useEffect(() => {
        if (postBin?.result) {
            setFormRedirect(true);
            history.push(`/forms/${docType}/start`);
        }
        //eslint-disable-next-line
    }, [postBin?.result]);

    useEffect(() => {
        if (deleteDoc?.result) {
            goBack();
            setSnackOpen(true);
            setSnackProps({severity: 'success', message: 'Document Deleted'});
        }
        // eslint-disable-next-line
    }, [deleteDoc?.result]);

    useEffect(() => {
        if (deleteDoc?.error) {
            setSnackOpen(true);
            setSnackProps({
                severity: 'warning',
                message: 'Something went wrong. Please try again later',
            });
        }
        // eslint-disable-next-line
    }, [deleteDoc?.error]);

    return (
        <Box display='flex'>
            <Box flexGrow={1}>
                <H3>{title}</H3>
            </Box>
            <Box>
                <IconButton onClick={e => setAnchorEl(e.currentTarget)}>
                    <MoreHoriz />
                </IconButton>
                <Popover
                    id='more-actions'
                    open={Boolean(anchorEl)}
                    onClose={() => setAnchorEl(null)}
                    anchorEl={anchorEl}
                    anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
                    transformOrigin={{vertical: 'top', horizontal: 'right'}}
                >
                    <List>
                        {docType === 'cg719s' && (
                            <ListItem
                                button
                                onClick={handleEdit}
                                disabled={postBin?.submitting}
                            >
                                <ListItemIcon>
                                    {postBin?.submitting ? (
                                        <CircularProgress />
                                    ) : (
                                        <Edit color='primary' />
                                    )}
                                </ListItemIcon>
                                <ListItemText primary='Edit'></ListItemText>
                            </ListItem>
                        )}
                        <ListItem button onClick={() => setShowConfirm(true)}>
                            <ListItemIcon>
                                <Delete color='secondary' />
                            </ListItemIcon>
                            <ListItemText primary='Delete'></ListItemText>
                        </ListItem>
                    </List>
                </Popover>
                <ConfirmPrompt
                    open={showConfirm}
                    title='Confirm Delete'
                    description={`Are you sure you want to delete this ${typeToTitle[docType]}?`}
                    onConfirm={handleConfirmDelete}
                    onCancel={() => setShowConfirm(false)}
                />
                {deleteDoc?.submissionInProgress && (
                    <Alert>
                        Deleting...
                        <CircularProgress />
                    </Alert>
                )}
            </Box>
            <Box>
                <IconButton edge='end' onClick={goBack}>
                    <CloseIcon />
                </IconButton>
            </Box>
        </Box>
    );
};

const Detail = ({history}) => {
    const {docType, docId} = useParams();
    useBrowserTitle(`| Document Detail ${typeToTitle[docType] || ''}`);
    const {data, isLoading, error} = useGetUserForm(docType, docId);

    let fields;
    let form = data;
    switch (docType) {
        case 'courses':
            fields = require('../forms/trainingcert/fields.js').default;
            break;
        case 'supporting_document':
            fields = require('../forms/supporting/fields.js').default;
            break;
        case 'uscg_aops':
            fields = require('../forms/uscgaops/fields.js').default;
            break;
        case 'tankerman':
            form = data?.tankerman;
            fields = require('../forms/tankerman/fields.js').default;
            break;
        default:
            debugLogger.warn(
                `Document detail fields path not specified for docType: ${docType}, using default`
            );
            fields = require(`../forms/${docType}/fields.js`).default;
            break;
    }

    const {user_document_id, confirmed, ...shownFields} =
        form?.form_data || form?.supporting_document || form || {};

    const goBack = () => {
        history.goBack();
    };

    const headerProps = {
        title: shownFields?.vessel_name,
        docType: docType,
        docId: docId,
        formData: form,
        goBack: goBack,
    };

    return (
        <Box
            style={{
                paddingTop: '1em',
                height: '100%',
                overflowX: 'auto',
                overflowY: 'hidden',
            }}
        >
            <PageTab
                isLoading={isLoading}
                error={error}
                unauthorized={form?.status === 'error'}
            >
                <WidgetCard title={typeToTitle[docType]} alignTitle='left'>
                    <HeaderBar {...headerProps} />
                    {docType === 'ssl' ? (
                        <SSLDisplay
                            state={{
                                ...shownFields,
                                user_document_post_response: {
                                    user_images: form?.user_images,
                                },
                            }}
                        />
                    ) : (
                        <FormReview
                            isEnd
                            isDocumentDetail
                            aops={docType === 'uscg_aops'}
                            allowEdit={false}
                            state={{
                                ...shownFields,
                                user_document_post_response: {
                                    user_images: form?.user_images,
                                },
                            }}
                            fields={fields}
                        />
                    )}
                </WidgetCard>
            </PageTab>
        </Box>
    );
};

export default withRouter(Detail);
